import React, { createContext, useState, useContext, useMemo } from "react";

// Create CartContext
const CartContext = createContext();

// Provide the CartContext to the app
export const useCart = () => {
  return useContext(CartContext);
};

// CartContext Provider
export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  // Calculate total price
  const totalPrice = useMemo(() => {
    return cartItems.reduce((acc, item) => {
      const price = parseFloat(item.price) || 0; // Ensure price is a valid number (fallback to 0 if invalid)
      const quantity = parseInt(item.quantity) || 0; // Ensure quantity is a valid number (fallback to 0 if invalid)
      return acc + price * quantity;
    }, 0).toFixed(2); // Ensure it has 2 decimal places
  }, [cartItems]);

  const addToCart = (item) => {
    setCartItems((prevItems) => {
      const existingItemIndex = prevItems.findIndex(
        (cartItem) =>
          cartItem.id === item.id &&
          cartItem.fabric === item.fabric &&
          cartItem.rangeModel === item.rangeModel &&
          cartItem.optionalExtras === item.optionalExtras
      );

      if (existingItemIndex >= 0) {
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].quantity += item.quantity;
        return updatedItems;
      }

      return [...prevItems, item];
    });
  };

  const removeFromCart = (id, fabric, rangeModel, optionalExtras) => {
    setCartItems((prevItems) =>
      prevItems.filter(
        (item) =>
          item.id !== id ||
          item.fabric !== fabric ||
          item.rangeModel !== rangeModel ||
          item.optionalExtras !== optionalExtras
      )
    );
  };

  const updateCartItemQuantity = (id, fabric, rangeModel, optionalExtras, newQuantity) => {
    setCartItems((prevItems) =>
      prevItems.map((item) => {
        if (
          item.id === id &&
          item.fabric === fabric &&
          item.rangeModel === rangeModel &&
          item.optionalExtras === optionalExtras
        ) {
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        totalPrice,
        addToCart,
        removeFromCart,
        updateCartItemQuantity,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
